import { ShiftManagementEnumsRestDayStateEnum } from 'generated/resources/graphql'
import { computed, makeObservable } from 'mobx'
import type moment from 'moment'
import momentTimezone from 'moment-timezone'

import { dateToString, stringToDate } from 'modules/core/lib/dates'

import type View from './View'

export interface Props {
  id: number
  employeeId: number
  date: moment.Moment
  state: ShiftManagementEnumsRestDayStateEnum
  timezone: string | null
  employeeFullName: string
  employeeAvatar: string
}

export interface RestDayPayload {
  id: number
  employee_id: number
  date: string
  state: ShiftManagementEnumsRestDayStateEnum
  timezone: string
  employeeFullName: string
  employeeAvatar: string
}

class RestDay {
  id: number
  employeeId: number
  date: moment.Moment
  state: ShiftManagementEnumsRestDayStateEnum
  timezone: string | null
  employeeFullName: string
  employeeAvatar: string

  constructor(props: Props) {
    this.id = props.id
    this.employeeId = props.employeeId
    this.date = props.date
    this.state = props.state
    this.timezone = props.timezone
    this.employeeFullName = props.employeeFullName
    this.employeeAvatar = props.employeeAvatar

    makeObservable(this, {
      isDraft: computed,
      isPublished: computed,
      dateWithTZ: computed,
      currentDate: computed,
      hours: computed,
    })
  }

  static build(props: RestDayPayload): RestDay {
    return new RestDay({
      id: props.id,
      employeeId: props.employee_id,
      date: stringToDate(props.date, 'backend_datetime'),
      state: props.state,
      timezone: props.timezone || null,
      employeeFullName: props.employeeFullName,
      employeeAvatar: props.employeeAvatar,
    })
  }

  get isDraft(): boolean {
    return this.state === 'draft'
  }

  get isPublished(): boolean {
    return this.state === 'published'
  }

  get dateWithTZ(): momentTimezone.Moment {
    return momentTimezone.tz(this.date, this.timezone ?? '')
  }

  get currentDate(): moment.Moment {
    return !this.timezone ? this.date : this.dateWithTZ
  }

  get hours(): moment.Moment[] {
    const startAt = this.currentDate
    const endAt = this.currentDate.clone().add(24, 'h')
    const hours: moment.Moment[] = []
    let hour = startAt

    // Only less than, and not equal to keep the endAt out.
    // Ex. 8:00 - 10:00 => 8:00, 9:00 but 10:00 is out of the range
    while (hour < endAt) {
      hours.push(hour)
      hour = hour.clone().add(1, 'h')
    }

    return hours
  }

  isDate(date: moment.Moment): boolean {
    return dateToString(this.currentDate, 'backend') === dateToString(date, 'backend')
  }

  inRange(firstDay: moment.Moment, lastDay: moment.Moment): boolean {
    return this.currentDate.isBetween(firstDay, lastDay, 'days', '[]')
  }

  datesByView(view: View): moment.Moment[] {
    if (view.isDaily) return this.hours

    return [this.currentDate]
  }
}

export default RestDay
